/**
 * WorkshopEquipmentList Component (#2323)
 *
 * Lists non-lift workshop equipment on the "Muut laitteet" sub-tab.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useWorkshopEquipmentList,
  useDeleteWorkshopEquipment,
  type WorkshopEquipment,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { WorkshopEquipmentFormDialog } from './WorkshopEquipmentFormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/common';
import { useAuthStore } from '@/stores/authStore';

export function WorkshopEquipmentList() {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Equipment mutations require equipment.manage server-side; hide what would 403.
  const canManageEquipment = hasPermission('equipment.manage');
  const [search, setSearch] = useState('');
  const [editEquipment, setEditEquipment] = useState<WorkshopEquipment | null>(null);
  const [deleteEquipment, setDeleteEquipment] = useState<WorkshopEquipment | null>(null);

  const { data, isLoading, isError } = useWorkshopEquipmentList({
    q: search || undefined,
    // Deleting is a soft delete — without this the removed row comes straight back.
    isActive: true,
    limit: 50,
  });

  const deleteMutation = useDeleteWorkshopEquipment();

  const equipment = data?.pages.flatMap((page) => page.data) ?? [];

  const handleConfirmDelete = async () => {
    if (!deleteEquipment) return;

    try {
      await deleteMutation.mutateAsync(deleteEquipment.id);
      notify.success(
        t('equipmentList.deleted'),
        t('equipmentList.deletedDesc', { name: deleteEquipment.name }),
      );
      setDeleteEquipment(null);
    } catch {
      notify.error(tc('error'), t('equipmentList.deleteFailed'));
    }
  };

  const sortCompareFn = (a: WorkshopEquipment, b: WorkshopEquipment, key: string): number => {
    switch (key) {
      case 'identifier':
        return a.identifier.localeCompare(b.identifier, 'fi');
      case 'name':
        return a.name.localeCompare(b.name, 'fi');
      case 'location':
        return (a.location ?? '').localeCompare(b.location ?? '', 'fi');
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<WorkshopEquipment>[] = [
    {
      header: t('equipmentList.identifier'),
      sortKey: 'identifier',
      renderCell: (item) => (
        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
          {item.identifier}
        </Typography>
      ),
    },
    {
      header: t('equipmentList.name'),
      sortKey: 'name',
      renderCell: (item) => (
        <Typography variant="body2">
          {item.name}
        </Typography>
      ),
    },
    {
      header: t('equipmentList.location'),
      sortKey: 'location',
      renderCell: (item) => (
        <Typography variant="body2">
          {item.location ?? '-'}
        </Typography>
      ),
    },
    {
      header: t('equipmentList.description'),
      renderCell: (item) => (
        <Typography variant="body2" color="text.secondary">
          {item.description ?? '-'}
        </Typography>
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (item) => canManageEquipment && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          <Tooltip title={tc('edit')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setEditEquipment(item); }}
              aria-label={tc('edit')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('delete')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setDeleteEquipment(item); }}
              color="error"
              aria-label={tc('delete')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        {t('equipmentList.loadFailed')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, alignItems: 'flex-end' }}
      >
        <TextField
          placeholder={t('equipmentList.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          size="small"
          sx={{ width: 250 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      {/* Table */}
      <DataTable<WorkshopEquipment>
        columns={columns}
        data={equipment}
        getRowKey={(item) => item.id}
        onRowClick={(item) => { setEditEquipment(item); }}
        sortCompareFn={sortCompareFn}
        defaultSortKey="identifier"
        loading={isLoading}
        emptyMessage={t('equipmentList.noEquipment')}
        ariaLabel={t('equipmentList.title')}
        renderMobileCard={(item) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                  {item.identifier}
                </Typography>
                <Typography variant="body2">
                  {item.name}
                </Typography>
              </Box>
              {canManageEquipment && (
                <Box>
                  <Tooltip title={tc('edit')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditEquipment(item); }} aria-label={tc('edit')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('delete')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteEquipment(item); }} color="error" aria-label={tc('delete')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            {(item.location ?? item.description) && (
              <Typography variant="caption" color="text.secondary">
                {[item.location, item.description].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        )}
      />

      {/* Edit Dialog */}
      {editEquipment && (
        <WorkshopEquipmentFormDialog
          open={!!editEquipment}
          equipment={editEquipment}
          onClose={() => { setEditEquipment(null); }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteEquipment}
        title={t('equipmentList.deleteTitle')}
        message={t('equipmentList.deleteConfirm', { name: deleteEquipment?.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => { setDeleteEquipment(null); }}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
